/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import os from 'os';
import path from 'path';
import pino, { DestinationStream, multistream, stdTimeFunctions } from 'pino';

import pkg from '@/../package.json';

const hostname = os.hostname(),
  pid = process.pid;

/**
 * A writable stream that is used to write log messages to.
 * In development, logs are prettified and printed to the console.
 * In production, logs are written to both a log file and stdout (raw JSON format).
 */
let pinoStream: DestinationStream = null;

if (typeof window === 'undefined') {
  if (process.env.NODE_ENV === 'development') {
    pinoStream = require('./pretty-transport').default;
  } else {
    const streams: DestinationStream[] = [process.stdout];

    if (process.env.LOG_DIR && process.env.LOG_FILE) {
      const logPath = path.join(process.env.LOG_DIR, process.env.LOG_FILE);
      const fileStream = pino.destination({ dest: logPath, sync: false });
      streams.push(fileStream);
    }

    pinoStream = multistream(streams);
  }
}

/**
 * Retrieves the current log level from the environment.
 */
const getLogLevel = (): string => {
  if (typeof window === 'undefined' && process.env.TRACE === 'true') {
    return 'trace';
  } else if (process.env.NODE_ENV === 'development') {
    return 'debug';
  }
  return 'info';
};

/**
 * Retrieves the appropriate time method for the current environment.
 */
const getTimeFn = () => {
  if (process.env.NODE_ENV === 'development') {
    // On development, we use epoch format, so we `pino-pretty` can translate it
    // to a human-readable format.
    return stdTimeFunctions.epochTime;
  }

  // On other environments, a unix time format would be the way to go
  // so tools like Elasticsearch can read the time field without any additional processing.
  return stdTimeFunctions.unixTime;
};

/**
 * Creates an instance of pino
 */
const createLogger = () => {
  return pino(
    {
      base: {
        pid,
        hostname,
        app: {
          name: 'workshops',
          version: pkg.version,
          brand: process.env.BRAND,
          ...(typeof window === 'undefined' && {
            env: process.env.ENVIRONMENT,
          }),
        },
      },
      level: getLogLevel(),
      timestamp: getTimeFn(),
      messageKey: 'msg',
    },
    pinoStream,
  );
};

const logger = createLogger();

export default logger;
